import setStyles from '../task/setStyles';

/** Optionen für die programmatische Gestaltung der Scrollbar. CSS sollte bevorzugt werden.
 * @typedef {Object} SimpleScroller~StyleOptions
 * @property {(number|string)=} width
 * @property {number=} opacity
 * @property {number=} right
 * @property {string=} trackColor
 * @property {string=} barColor
 * @property {boolean=} fixedBarHeight Eine fixierte Höhe der Scrollbar auf 1 Pixel ist nur über diese Option möglich
 */
const defaultStyleOptions = {
    width: null,
    opacity: null,
    right: null,
    trackColor: null,
    barColor: null,
    fixedBarHeight: false
};

const {
    HTMLElement, document, parseInt, Number, TouchEvent, Math, getComputedStyle
} = globalThis;
const scrollers = [];

let movingScroller = null;

const addNamespacedClass = (element, className) =>
{
    element.classList.add(`simple-scroller-${className}`);
};

const removeNamespacedClass = (element, className) =>
{
    element.classList.remove(`simple-scroller-${className}`);
};

const getEventY = evt => (evt.targetTouches ? evt.targetTouches[0].clientY : evt.clientY);

const downHandler = (evt) =>
{
    const {target} = evt;
    const {classList} = target;

    if (!classList) { return; }

    if (classList.contains('simple-scroller-track'))
    {
        // eslint-disable-next-line no-use-before-define
        const scroller = SimpleScroller.getByContainer(target.previousSibling);
        scroller.moveBarTo(
            (
                (getEventY(evt) - target.getBoundingClientRect().top - (scroller.barHeight / 2)) /
                scroller.scale
            ) + document.body.scrollTop
        );
    }
    else if (classList.contains('simple-scroller-bar'))
    {
        if (TouchEvent && evt instanceof TouchEvent)
        {
            evt.preventDefault();
        }
        // eslint-disable-next-line no-use-before-define
        movingScroller = SimpleScroller.getByContainer(
            target.parentNode.previousSibling
        );
        movingScroller.startY = getEventY(evt);
        movingScroller.startBarTop = parseInt(target.style.top, 10);
        setStyles(movingScroller.parent, {
            msUserSelect: 'none', webkitUserSelect: 'none', userSelect: 'none'
        });
    }
};

const moveHandler = (evt) =>
{
    if (movingScroller === null) { return; }
    evt.preventDefault();
    const delta = (getEventY(evt) - movingScroller.startY) / movingScroller.scale;
    movingScroller.moveBarTo(movingScroller.startBarTop + delta);
};

const upHandler = (evt) =>
{
    if (movingScroller === null) { return; }
    evt.preventDefault();
    movingScroller.updateBar();
    setStyles(movingScroller.parent, {
        msUserSelect: null, webkitUserSelect: null, userSelect: null
    });
    movingScroller = null;
};

const addGlobalEventListeners = () =>
{
    document.addEventListener('touchstart', downHandler, {passive: false});
    document.addEventListener('mousedown', downHandler);
    document.addEventListener('touchmove', moveHandler, {passive: false});
    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('touchend', upHandler, {passive: false});
    document.addEventListener('mouseup', upHandler);
};

const removeGlobalEventListeners = () =>
{
    document.removeEventListener('touchstart', downHandler, {passive: false});
    document.removeEventListener('mousedown', downHandler);
    document.removeEventListener('touchmove', moveHandler, {passive: false});
    document.removeEventListener('mousemove', moveHandler);
    document.removeEventListener('touchend', upHandler, {passive: false});
    document.removeEventListener('mouseup', upHandler);
};

class SimpleScroller
{
    static getByContainer(container)
    {
        return scrollers.find(scroller => scroller.container === container);
    }

    static refreshAll()
    {
        scrollers.forEach(scroller => scroller.refresh());
    }

    /**
     * @desc Funktioniert nur, wenn auch die Styles importiert werden: ``@import '~kids-lib/scss/_simpleScroller';``
     * @param {HTMLElement} container Der Inhalt des Containers wird gescrollt. Das Elternelement des Containers muss
     * einige Voraussetzungen erfüllen, damit die Standard-Scrollbar des Containers versteckt werden kann. Außerdem
     * wird die gestylte Scrollbar in das Elternelement eingefügt.
     * @param {SimpleScroller~StyleOptions=} styleOptions
     * @see https://stackoverflow.com/questions/16670931/hide-scroll-bar-but-while-still-being-able-to-scroll
     * @see https://stackoverflow.com/questions/21064101/understanding-offsetwidth-clientwidth-scrollwidth-and-height-respectively
     */
    constructor(container, styleOptions = false)
    {
        const parent = container.parentNode;

        if (
            !(container instanceof Object) || !(container instanceof HTMLElement) ||
            !parent
        )
        {
            throw new Error('Container or its parent is no HTMLElement.');
        }

        if (SimpleScroller.getByContainer(container))
        {
            throw new Error('Container has already a SimpleScroller instance.');
        }

        const parentStyles = getComputedStyle(parent);
        const containerStyles = getComputedStyle(container);

        if (containerStyles.position === 'absolute' || containerStyles.position === 'fixed')
        {
            throw new Error('Container can only have a static or relative position.');
        }

        if (parseInt(containerStyles.marginRight))
        {
            throw new Error('Container shouldn\'t have a right margin.');
        }

        if (parseInt(parentStyles.paddingRight))
        {
            throw new Error('Parent of container shouldn\'t have a right padding.');
        }

        addNamespacedClass(parent, 'parent');
        addNamespacedClass(container, 'container');

        const track = document.createElement('div');
        addNamespacedClass(track, 'track');

        const bar = document.createElement('div');
        addNamespacedClass(bar, 'bar');

        track.appendChild(bar);
        parent.insertBefore(track, container.nextSibling);

        this._parent = container.parentNode;
        this._container = container;
        this._track = track;
        this._bar = bar;
        this._scrollRatio = null;
        this._trackScrollRatio = 0;
        this._barHeight = 0;
        this._maxBarTop = 0;
        this._maxScrollTop = 0;
        this._fixedBarHeight = false;
        this._scale = 1;

        if (styleOptions)
        {
            styleOptions = {...defaultStyleOptions, ...styleOptions};
            setStyles(track, {
                width: styleOptions.width,
                opacity: styleOptions.opacity,
                right: styleOptions.right,
                backgroundColor: styleOptions.trackColor
            });
            setStyles(bar, {backgroundColor: styleOptions.barColor});
            this._fixedBarHeight = styleOptions.fixedBarHeight;
        }

        this.refresh();

        this._scrollHandler = this.updateBar.bind(this);
        container.addEventListener('scroll', this._scrollHandler);
        this.scrollTo(0);

        if (scrollers.length === 0)
        {
            addGlobalEventListeners();
        }

        scrollers.push(this);
    }

    get parent()
    {
        return this._parent;
    }

    get container()
    {
        return this._container;
    }

    get containerVisible()
    {
        return this._container.offsetWidth > 0;
    }

    get barHeight()
    {
        return this._barHeight;
    }

    get scale()
    {
        return this._scale;
    }

    set scale(scale)
    {
        this._scale = scale;
    }

    /** Muss aufgerufen werden, wenn sich die Höhe des scrollbaren Inhalts im Container ändert.
     * @return {SimpleScroller}
     */
    refresh()
    {
        if (!this.containerVisible) { return this; }

        const container = this._container;

        if (
            this._scrollRatio === null &&
            this._parent.clientWidth !== container.offsetLeft + container.offsetWidth
        )
        {
            throw new Error('Right side of container should align with right side of parent.');
        }
        removeNamespacedClass(this._parent, 'visible');

        const containerStyles = getComputedStyle(container);
        const scrollBarWidth = container.offsetWidth - container.clientWidth -
            parseInt(containerStyles.borderLeftWidth) - parseInt(containerStyles.borderRightWidth);

        if (scrollBarWidth) { setStyles(container, {marginRight: -scrollBarWidth}); }

        const trackStyles = {
            top: container.offsetTop + parseInt(containerStyles.borderTopWidth, 10),
            height: container.clientHeight
        };

        const containerIndex = parseInt(containerStyles.zIndex, 10);
        if (!Number.isNaN(containerIndex))
        {
            trackStyles.zIndex = containerIndex + 1;
        }

        setStyles(this._track, trackStyles);

        this._scrollRatio = container.clientHeight / container.scrollHeight;
        if (this._scrollRatio >= 1)
        {
            removeNamespacedClass(this._parent, 'visible');
            return this;
        }
        addNamespacedClass(this._parent, 'visible');
        this._scrollRatio = container.clientHeight / container.scrollHeight;

        const trackHeight = this._track.clientHeight;
        this._barHeight = Math.floor(trackHeight * this._scrollRatio);
        this._trackScrollRatio = trackHeight / container.scrollHeight;

        if (this._fixedBarHeight)
        {
            this._trackScrollRatio = trackHeight /
                (container.scrollHeight - (this._barHeight / this._trackScrollRatio));
            this._barHeight = 1;
        }

        this._maxBarTop = trackHeight - this._barHeight;
        this._maxScrollTop = container.scrollHeight - container.clientHeight;

        setStyles(this._bar, {height: this._barHeight});
        this.updateBar();

        return this;
    }

    updateBar()
    {
        let {scrollTop} = this._container;

        if (scrollTop < 0)
        {
            scrollTop = 0;
        }
        else if (scrollTop > this._maxScrollTop)
        {
            scrollTop = this._maxScrollTop;
        }

        this._bar.style.top = `${Math.ceil(scrollTop * this._trackScrollRatio)}px`;
        return this;
    }

    /** Setzt die Top-Position der Scrollbar in Pixeln und scrollt den Inhalt entsprechend.
     * @param {integer} top
     * @return {SimpleScroller}
     */
    moveBarTo(top)
    {
        if (top < 0) { top = 0; }
        else if (top > this._maxBarTop) { top = this._maxBarTop; }
        this.scrollTo(top / this._trackScrollRatio);
        return this;
    }

    /** Scrollt den Inhalt.
     * @param {integer} top
     * @return {SimpleScroller}
     */
    scrollTo(top)
    {
        this._container.scrollTop = top;
        return this;
    }

    /** Der Scroller sollte zerstört werden, wenn er nicht mehr gebraucht wird. */
    destroy()
    {
        removeNamespacedClass(this._parent, 'parent');
        removeNamespacedClass(this._container, 'container');

        this._parent.removeChild(this._track);
        this._container.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;

        scrollers.splice(scrollers.indexOf(this), 1);

        if (scrollers.length === 0)
        {
            removeGlobalEventListeners();
        }
    }
}

export default SimpleScroller;
